/* eslint-disable @typescript-eslint/no-explicit-any */
import { DualMoney, MoneyValue } from "../types";
import { platform } from "../platform";

type DateSize = "sm" | "md" | "lg";

const dateFormatMap: Record<DateSize, Intl.DateTimeFormatOptions> = {
  sm: {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  },
  md: {
    day: "numeric",
    month: "short",
    year: "numeric",
  },
  lg: {
    day: "numeric",
    month: "long",
    year: "numeric",
  },
};

/**
 * Format a date to a string
 * @param date - The date to format
 * @default date - Current Date
 * @returns The formatted date
 */
export function fDate(date: string | Date = "now", size: DateSize = "lg") {
  if (date === "now") date = new Date();
  if (!date) return "";

  return new Date(date).toLocaleDateString("en-GB", dateFormatMap[size]);
}

export function fDateInput(date?: Date | null | string) {
  return date ? new Date(date).toISOString().split("T")[0] : "";
}

/**
 * Format a date to a time string
 * @param date - The date to format
 * @default date - Current Date
 * @returns The formatted time
 */
export const fTime = (date: string | Date = "now") => {
  if (date === "now") date = new Date();
  if (!date) return "";
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Count the number of items in an array or a number
 * @param input - The array or number to count
 * @param word - The word to pluralize
 * @param nullText - The text to return if the count is 0
 * @returns The pluralized word
 */
export function cnt(
  input: Array<unknown> | number,
  word: string,
  nullText = "---",
): string {
  const count = Array.isArray(input) ? input.length : input;
  return count > 0 ? (count === 1 ? word : `${word}s`) : nullText;
}

/**
 * Format a date to a relative time
 * @param dateString - The date to format
 * @returns The formatted date
 */
export const rTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInDays === 0) {
    return "Today";
  } else if (diffInDays === 1) {
    return "Yesterday";
  } else if (diffInDays < 7) {
    return `${diffInDays} ${cnt(diffInDays, "day")} ago`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks} ${cnt(diffInWeeks, "week")} ago`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths} ${cnt(diffInMonths, "month")} ago`;
  } else {
    return `${diffInYears} ${cnt(diffInYears, "year")} ago`;
  }
};

/**
 * Formats a number into Nigerian Naira currency string.
 * @param amount The number to format (e.g., 123400000.00 or -13400000.00).
 * @returns The formatted string (e.g., "₦123,400,000.00" or "-₦13,400,000.00").
 */
export function formatNaira(amount: number): string {
  // Use the Intl.NumberFormat API for reliable currency formatting
  // 'en-NG' uses the Naira symbol (₦) and correct punctuation for numbers.
  const formatter = new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // The formatter handles commas, decimal points, and the currency symbol.
  // It will also handle negative numbers correctly (often placing the minus sign before the currency symbol).
  return formatter.format(amount);
}

export function fMoney(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

/**
 * Returns the relevant money value based on the current platform
 */
export function getDMoney(
  dual?: DualMoney,
  currency?: "usd" | "ngn",
): MoneyValue | undefined {
  if (!dual) return undefined;
  if (currency) return dual[currency];
  return platform.isFarmer || platform.isAdmin ? dual.ngn : dual.usd;
}

/**
 * Formats a dual money object based on the current platform
 */
export function fDMoney(
  dual?: DualMoney,
  suffix = "",
  currency?: "usd" | "ngn",
): string {
  const money = getDMoney(dual, currency);
  if (!money) return "---";
  const moneyStr = fMoney(money.amount, money.currency);
  return `${moneyStr}${suffix}`;
}

/**
 * Replaces common separators with spaces and capitalizes the first letter of each word
 * @param str - The string to replace and capitalize
 * @returns The string with common separators replaced by spaces and each word capitalized
 */
export function replaceAndCap(str?: string | null): string {
  if (!str) return "";
  return str
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function fDates(obj: any): any {
  if (!obj) return obj;

  if (typeof obj === "string" && isISODate(obj)) {
    return toYYYYMMDD(obj);
  }

  if (obj instanceof Date) {
    return toYYYYMMDD(obj.toISOString());
  }

  if (Array.isArray(obj)) {
    return obj.map(fDates);
  }

  if (typeof obj === "object") {
    return Object.entries(obj).reduce((acc, [key, val]) => {
      acc[key] = fDates(val);
      return acc;
    }, {} as any);
  }

  return obj;
}

function toYYYYMMDD(isoString: string): string {
  return isoString.split("T")[0];
}

function isISODate(str: string): boolean {
  const iso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  if (!iso.test(str)) return false;

  const date = new Date(str);
  return !isNaN(date.getTime());
}

/** The display unit for weight/quantity. Change this to update all labels site-wide. */
export const UNIT = "MT";

/** Suffix for per-unit price display, e.g. "$1,000/MT" */
export const PER_UNIT = `/${UNIT}`;

/** Format a quantity with the weight unit, e.g. "25 MT" */
export function fTon(
  qty: number | string,
  separator: " " | "/" | " per " = " ",
): string {
  return `${qty}${separator}${UNIT}`;
}

export function f9jaPhone(value: string): string {
  // Remove any spaces, dashes, or +234 prefix if present
  const cleaned = value.replace(/[\s\-+]/g, "").replace(/^234/, "");

  // If 11 digits and starts with 0, remove the 0
  if (cleaned.length === 11 && cleaned.startsWith("0")) {
    return `+234${cleaned.slice(1)}`;
  }

  // If 10 digits, just append +234
  if (cleaned.length === 10) {
    return `+234${cleaned}`;
  }

  // Return as-is if already has +234
  if (value.startsWith("+234")) {
    return value;
  }

  return value;
}
